import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Grid, Button, useTheme, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useParams, useNavigate } from "react-router-dom";
import MemoryLeakDetails from "./MemoryDetails";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

const MemoryLeak = () => {
  const [lineChartData, setLineChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [hasLeaks, setHasLeaks] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [dataSource, setDataSource] = useState("multiple_runs");
  const [chartType, setChartType] = useState("memory_usage");
  const [chartDataCache, setChartDataCache] = useState({});
  const { projectId } = useParams();
  const token = localStorage.getItem("access_token");
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchMemoryLeakData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/valgrind-memleak-result?project_id=${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const data = await response.json();
        processMemoryLeakData(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching memory leak data:", error);
        setNoData(true);
        setLoading(false);
      }
    };

    fetchMemoryLeakData();
  }, [projectId]);

  const handleDownloadPDF = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/reports/latest-memoryleak-report?project_id=${projectId}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch report information");
      }

      const data = await response.json();
      console.log("API Response:", data);

      if (data && data.presigned_url) {
        const link = document.createElement("a");
        link.href = data.presigned_url;
        link.download = `${activeTool === 'renode' ? 'Dynamic_MemoryLeak' : 'MemoryLeak'}_Report_${projectId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("PDF generation failed. No presigned URL found.");
        console.error("Presigned URL missing in API response:", data);
      }
    } catch (error) {
      console.error("Error during download:", error);
      alert("Failed to download the report. Please try again later.");
    }
  };

  const processMemoryLeakData = (data) => {
    let memoryResults = [];
    let tool = null;
    
    if (data.renodeMemLeakResult && data.renodeMemLeakResult.length > 0) {
      memoryResults = data.renodeMemLeakResult;
      tool = "renode";
    } else if (data.valgrindMemLeakResult && data.valgrindMemLeakResult.length > 0) {
      memoryResults = data.valgrindMemLeakResult;
      tool = "valgrind";
    }

    setActiveTool(tool);

    if (memoryResults.length === 0) {
      setNoData(true);
      return;
    }

    const sortedResults = memoryResults.sort((a, b) => {
      const dateA = a.memoryTestDate ? parseFloat(a.memoryTestDate) : 0;
      const dateB = b.memoryTestDate ? parseFloat(b.memoryTestDate) : 0;
      return dateB - dateA;
    });

    const latestRun = sortedResults[0];

    let hasMemoryLeaks = false;
    if (tool === "valgrind") {
      hasMemoryLeaks = memoryResults.some(
        (result) =>
          parseInt(
            result.memoryTestResult?.leakSummary?.definitelyLostBytes || 0,
            10
          ) > 0
      );
    } else if (tool === "renode") {
      hasMemoryLeaks = memoryResults.some(
        (result) =>
          parseInt(
            result.memoryTestResult?.leakSummary?.definitelyLostBytes || 0,
            10
          ) > 0 ||
          parseInt(
            result.memoryTestResult?.dynamicAnalysis?.summary?.leaked_bytes || 0,
            10
          ) > 0
      );
    }

    setHasLeaks(hasMemoryLeaks);

    if (tool === "renode" && 
        latestRun.all_runs_data && 
        latestRun.all_runs_data.length > 1) {
      setDataSource("all_runs");
      generateAllChartData(latestRun.all_runs_data, true);
    } else {
      setDataSource("multiple_runs");
      generateAllChartData(sortedResults.slice(0, 10), false);
    }
  };

  const generateAllChartData = (data, isAllRunsData) => {
    if (!data || data.length === 0) {
      setNoData(true);
      return;
    }

    setNoData(false);

    const labels = [];
    const memoryUsageData = {
      allocated: [],
      freed: [],
      inUse: [],
      leaks: [],
    };
    
    const leakAnalysisData = {
      definitelyLost: [],
      indirectlyLost: [],
      possiblyLost: [],
      leakedBlocks: [],
    };
    
    const securityData = {
      unsafeFunctions: [],
    };
    
    const staticAnalysisData = {
      ramPercent: [],
      flashPercent: [],
    };

    data.forEach((item, index) => {
      let analysisResults, heapSummary, leakSummary, dynamicSummary, staticAnalysis, unsafeFunctions;
      
      if (isAllRunsData) {
        analysisResults = item.analysis_results;
        heapSummary = analysisResults?.heapSummary || {};
        leakSummary = analysisResults?.leakSummary || {};
        dynamicSummary = analysisResults?.dynamicAnalysis?.summary || {};
        staticAnalysis = analysisResults?.staticAnalysis || {};
        unsafeFunctions = analysisResults?.unsafeFunctions?.count || 0;
      } else {
        heapSummary = item.memoryTestResult?.heapSummary || {};
        leakSummary = item.memoryTestResult?.leakSummary || {};
        dynamicSummary = item.memoryTestResult?.dynamicAnalysis?.summary || {};
        staticAnalysis = item.memoryTestResult?.staticAnalysis || {};
        unsafeFunctions = item.memoryTestResult?.unsafeFunctions?.count || 0;
      }

      // Labels
      if (isAllRunsData) {
        labels.push(`Run ${item.run_number}`);
      } else {
        labels.push(`Run ${index + 1}`);
      }

      // Memory Usage Data
      memoryUsageData.allocated.push(parseInt(heapSummary.totalBytesAllocated || dynamicSummary.total_allocated || 0, 10));
      memoryUsageData.freed.push(parseInt(heapSummary.totalBytesFreed || dynamicSummary.total_freed || 0, 10));
      memoryUsageData.inUse.push(parseInt(heapSummary.inUseBytes || dynamicSummary.leaked_bytes || 0, 10));
      memoryUsageData.leaks.push(parseInt(leakSummary.definitelyLostBytes || dynamicSummary.leaked_bytes || 0, 10));

      // Leak Analysis Data
      leakAnalysisData.definitelyLost.push(parseInt(leakSummary.definitelyLostBytes || 0, 10));
      leakAnalysisData.indirectlyLost.push(parseInt(leakSummary.indirectlyLostBytes || 0, 10));
      leakAnalysisData.possiblyLost.push(parseInt(leakSummary.possiblyLostBytes || 0, 10));
      leakAnalysisData.leakedBlocks.push(parseInt(leakSummary.definitelyLostBlocks || dynamicSummary.leaked_blocks || 0, 10));

      // Security Data
      securityData.unsafeFunctions.push(parseInt(unsafeFunctions || 0, 10));

      // Static Analysis Data
      staticAnalysisData.ramPercent.push(parseFloat(staticAnalysis.ram_percent || 0));
      staticAnalysisData.flashPercent.push(parseFloat(staticAnalysis.flash_percent || 0));
    });

    // Cache all chart data
    const cache = {
      labels,
      memoryUsageData,
      leakAnalysisData,
      securityData,
      staticAnalysisData
    };

    setChartDataCache(cache);
    
    // Set initial chart data
    updateChartByType(chartType, cache);
  };

  const updateChartByType = (type, cache) => {
    if (!cache) return;

    const { labels, memoryUsageData, leakAnalysisData, securityData, staticAnalysisData } = cache;
    
    let datasets = [];
    let yAxisLabel = "";
    let useDualYAxis = false;

    switch (type) {
      case "memory_usage":
        datasets = [
          {
            label: "Total Allocated (MB)",
            data: memoryUsageData.allocated.map(bytes => bytes / 1024 / 1024),
            borderColor: "rgba(75, 192, 192, 1)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Total Freed (MB)",
            data: memoryUsageData.freed.map(bytes => bytes / 1024 / 1024),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "In Use (MB)",
            data: memoryUsageData.inUse.map(bytes => bytes / 1024 / 1024),
            borderColor: "rgba(255, 159, 64, 1)",
            backgroundColor: "rgba(255, 159, 64, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Leaked (MB)",
            data: memoryUsageData.leaks.map(bytes => bytes / 1024 / 1024),
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            fill: false,
            tension: 0.4,
          },
        ];
        yAxisLabel = "Memory (MB)";
        break;

      case "leak_analysis":
        datasets = [
          {
            label: "Definitely Lost (KB)",
            data: leakAnalysisData.definitelyLost.map(bytes => bytes / 1024),
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Indirectly Lost (KB)",
            data: leakAnalysisData.indirectlyLost.map(bytes => bytes / 1024),
            borderColor: "rgba(255, 206, 86, 1)",
            backgroundColor: "rgba(255, 206, 86, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Possibly Lost (KB)",
            data: leakAnalysisData.possiblyLost.map(bytes => bytes / 1024),
            borderColor: "rgba(153, 102, 255, 1)",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Leaked Blocks",
            data: leakAnalysisData.leakedBlocks,
            borderColor: "rgba(201, 203, 207, 1)",
            backgroundColor: "rgba(201, 203, 207, 0.2)",
            fill: false,
            tension: 0.4,
          },
        ];
        yAxisLabel = "Bytes (KB) / Blocks";
        break;

      case "security":
        datasets = [
          {
            label: "Unsafe Functions",
            data: securityData.unsafeFunctions,
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            fill: false,
            tension: 0.4,
          },
        ];
        yAxisLabel = "Count";
        break;

      case "static_analysis":
        datasets = [
          {
            label: "RAM Usage (%)",
            data: staticAnalysisData.ramPercent,
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            fill: false,
            tension: 0.4,
          },
          {
            label: "Flash Usage (%)",
            data: staticAnalysisData.flashPercent,
            borderColor: "rgba(153, 102, 255, 1)",
            backgroundColor: "rgba(153, 102, 255, 0.2)",
            fill: false,
            tension: 0.4,
          },
        ];
        yAxisLabel = "Percentage (%)";
        break;

      default:
        datasets = [];
        yAxisLabel = "";
    }

    setLineChartData({
      labels,
      datasets,
      yAxisLabel,
      useDualYAxis
    });
  };

  const handleChartTypeChange = (event) => {
    const newType = event.target.value;
    setChartType(newType);
    
    if (Object.keys(chartDataCache).length > 0) {
      updateChartByType(newType, chartDataCache);
    }
  };

  const getChartTitle = () => {
    const chartTitles = {
      memory_usage: "Memory Usage Analysis",
      leak_analysis: "Memory Leak Analysis",
      security: "Security Analysis",
      static_analysis: "Static Memory Analysis"
    };

    const baseTitle = chartTitles[chartType] || "Memory Analysis";
    
    if (activeTool === 'renode') {
      if (dataSource === 'all_runs') {
        return `${baseTitle} - All Runs Data`;
      } else {
        return `${baseTitle} - Multiple Run Results`;
      }
    } else {
      return baseTitle;
    }
  };

  // Simplified chart options without dual Y-axis
  const getChartOptions = () => ({
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: lineChartData?.yAxisLabel || ''
        }
      },
      x: {
        display: true,
        title: {
          display: true,
          text: dataSource === 'all_runs' ? 'Run Number' : 'Test Run'
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6">Loading memory analysis data...</Typography>
      </Box>
    );
  }

  if (!activeTool) {
    return (
      <Box p={3}>
        <Paper elevation={3} style={{ padding: "20px", textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            No memory leak data available
          </Typography>
          <Button
            variant="contained"
            className="athrv-dash-primary-button"
            sx={{ 
              mt: 2,
              backgroundColor: theme.palette.mode === 'dark' ? '#263d68' : undefined, 
            }}
            onClick={() => navigate(`/projects`)}
          >
            Back to Project List
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3} alignItems="stretch">
        {!noData && (
          <>
            <Grid item xs={8}>
              <Paper
                elevation={3}
                style={{
                  padding: "16px",
                  backgroundColor: hasLeaks ? "#ffebee" : "#e8f5e9",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body1"
                  style={{
                    color: hasLeaks ? "#d32f2f" : "#2e7d32",
                    fontWeight: "bold",
                  }}
                >
                  {hasLeaks
                    ? `⚠️ Memory leak detected in last run.`
                    : `✅ No memory leaks detected in last run.`}
                </Typography>
                
                {activeTool === 'renode' && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    {dataSource === 'all_runs' ? 'Using all runs data' : 'Using multiple run results'}
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={4}>
              <Paper
                elevation={3}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  className="athrv-dash-primary-button"
                  fullWidth
                  onClick={handleDownloadPDF}
                  style={{ height: "100%" }}
                >
                  Download {activeTool === 'renode' ? 'Dynamic MemoryLeak' : 'MemoryLeak'} Report
                </Button>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h6">
                {getChartTitle()}
              </Typography>
              
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    label="Chart Type"
                    onChange={handleChartTypeChange}
                  >
                    <MenuItem value="memory_usage">Memory Usage</MenuItem>
                    <MenuItem value="leak_analysis">Dynamic Leak Analysis</MenuItem>
                    <MenuItem value="security">Security Analysis</MenuItem>
                    <MenuItem value="static_analysis">Static Analysis</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  className="athrv-dash-primary-button"
                  sx={{ 
                    mb: { xs: 1, sm: 0 }, 
                    backgroundColor: theme.palette.mode === 'dark' ? '#263d68' : undefined, 
                  }}
                  onClick={() => navigate(`/projects`)}
                >
                  Back to Project List
                </Button>
              </Box>
            </Box>

            <div
              style={{
                height: "400px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              {noData ? (
                <Typography variant="body1" color="textSecondary">
                  No Data Available for {activeTool}
                </Typography>
              ) : (
                lineChartData && (
                  <Line
                    data={lineChartData}
                    options={getChartOptions()}
                  />
                )
              )}
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <Typography variant="h6" gutterBottom>
              {activeTool === 'renode' ? 'Dynamic Memory Analysis Summary' : 'Memory Leak Summary'}
            </Typography>
            {noData ? (
              <Typography variant="body1" color="textSecondary">
                No Data Available for {activeTool}
              </Typography>
            ) : (
              <MemoryLeakDetails />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemoryLeak;